// CompanyNewPage — форма создания компании (R17a, D14).
// После успешного create → редирект на /crm/companies/{id}.
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { companiesApi } from '@/api/companies';
import { useAuth } from '@/auth/AuthContext';
import { UserAutocomplete } from '@/components/UserAutocomplete';
import { isValidWebsite, normalizeWebsite } from '@/lib/website';
import { t } from '@/i18n';

/** Zod-схема валидации формы создания компании. */
const companyFormSchema = z.object({
  name: z.string().min(1, 'Название обязательно').max(200, 'Слишком длинное название'),
  industry: z.string().max(100, 'Слишком длинная отрасль').optional().or(z.literal('')),
  phone: z
    .string()
    .regex(/^$|^[+]?[0-9 ()-]{5,50}$/, 'Некорректный телефон')
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .regex(/^$|^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, 'Некорректный email')
    .optional()
    .or(z.literal('')),
  // U-22: мягкая валидация — схема https:// необязательна, TLD обязателен, latin-only.
  // Сайт без схемы принимается и нормализуется (https://-префикс) на blur / перед отправкой.
  website: z
    .string()
    .refine((v) => isValidWebsite(v), t('crm.companies.websiteInvalid'))
    .optional()
    .or(z.literal('')),
  address: z.string().max(2000, 'Слишком длинный адрес').optional().or(z.literal('')),
  taxId: z.string().regex(/^$|^[0-9]{10,12}$/, 'ИНН — 10 или 12 цифр'),
  ownerUserId: z.string().min(1, 'Владелец обязателен'),
  notes: z.string().max(4000, 'Слишком длинные заметки').optional().or(z.literal('')),
});

type CompanyFormValues = z.infer<typeof companyFormSchema>;

/** Страница создания компании. */
export function CompanyNewPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  const { control, handleSubmit } = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      name: '',
      industry: '',
      phone: '',
      email: '',
      website: '',
      address: '',
      taxId: '',
      ownerUserId: currentUser?.id ?? '',
      notes: '',
    },
  });

  // U-24: ref-флаг закрывает окно «двойной клик/Enter до ре-рендера isPending=true» —
  // useMutation отдаёт snapshot (useSyncExternalStore), в замыкании isPending не виден
  // до ререндера; кнопка disabled — вторая линия защиты.
  const submittingRef = useRef(false);
  const mutation = useMutation({
    mutationFn: (values: CompanyFormValues) =>
      companiesApi.create({
        name: values.name,
        industry: values.industry || null,
        website: values.website || null,
        phone: values.phone || null,
        email: values.email || null,
        address: values.address || null,
        taxId: values.taxId,
        notes: values.notes || null,
        ownerUserId: values.ownerUserId,
        // Backend derive'ит departmentId из текущего пользователя; поле требуется TS-контрактом.
        departmentId: currentUser?.departments[0]?.id ?? '',
      }),
    onSuccess: (company) => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      // U-29: селекторы построены на ['companies-map'] — инвалидируем обе ветки (иначе no-op + staleTime).
      void queryClient.invalidateQueries({ queryKey: ['companies-map'] });
      navigate(`/crm/companies/${company.id}`);
    },
    // U-24: после завершения мутации ref сбрасывается — форма снова submit-доступна.
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  const onSubmit: SubmitHandler<CompanyFormValues> = (values) => {
    if (submittingRef.current || mutation.isPending) return;
    submittingRef.current = true;
    mutation.mutate(values);
  };

  return (
    <Box>
      <PageHeader title="Новая компания" subtitle="Заполните обязательные поля и сохраните" />
      {mutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mutation.error instanceof Error ? mutation.error.message : 'Не удалось создать компанию'}
        </Alert>
      ) : null}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Название компании"
                required
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Название компании' }}
              />
            )}
          />
          <Controller
            name="industry"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Отрасль"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Отрасль' }}
              />
            )}
          />
          <Controller
            name="taxId"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="ИНН"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? '10 или 12 цифр'}
                inputProps={{ 'aria-label': 'ИНН' }}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Телефон"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Телефон компании' }}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Email"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Email компании' }}
              />
            )}
          />
          <Controller
            name="website"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Сайт"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? t('crm.companies.websiteHint')}
                onBlur={() => {
                  // U-22: автопрефикс https:// при уходе с поля (канон §5.3).
                  const next = normalizeWebsite(field.value ?? '');
                  if (next !== field.value) {
                    field.onChange(next);
                  }
                }}
                inputProps={{ 'aria-label': 'Сайт' }}
              />
            )}
          />
          <Controller
            name="address"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Адрес"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Адрес' }}
              />
            )}
          />
          {/* R36b / U-2: живой поиск владельца (OwnerUserSelect удалён). */}
          <Controller
            name="ownerUserId"
            control={control}
            render={({ field, fieldState }) => (
              <UserAutocomplete
                label="Владелец"
                value={field.value}
                onChange={field.onChange}
                required
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                aria-label="Владелец компании"
              />
            )}
          />
          <Controller
            name="notes"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Заметки"
                fullWidth
                multiline
                minRows={3}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Заметки компании' }}
              />
            )}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button type="submit" variant="contained" disabled={mutation.isPending}>
              Создать компанию
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
